import React from "react";
import { Waiting, ErrorMessage } from "components/ui";
import OrderProfileNew from "components/order-profile-new";
import { post } from "utils/api";
import { useParams, useLocation } from "react-router-dom";

const getOrderIds = (params, location) => {
  if (params && params.id) {
    return [params.id];
  }
  if (location && location.state) {
    return location.state.orderIds;
  }
};

// Orders per request. Kept well under the server's per-request cap
// (OrderProfileBatchLimit = 250). Smaller chunks return sooner, so the progress
// count advances smoothly and no single request holds a large slice of the
// server's connection pool. Selections larger than this are split across several
// requests rather than refused — the orders table can hold a few thousand rows
// and all of them may be selected.
const CHUNK_SIZE = 25;
// How many chunk requests are in flight at once. Enough to keep the wall clock
// down on a large job without piling requests onto the server.
const CONCURRENCY = 3;

const chunk = (items, size) => {
  const chunks = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
};

const BatchPrintOrders = () => {
  const params = useParams();
  const location = useLocation();
  // Fixed for the life of the page — the ids arrive once, via navigation state.
  const orderIdsRef = React.useRef(getOrderIds(params, location) ?? []);
  const orderIds = orderIdsRef.current;

  const [orders, setOrders] = React.useState([]);
  const [loadedCount, setLoadedCount] = React.useState(0);
  const [isFetching, setIsFetching] = React.useState(orderIds.length > 0);
  const [didOpenPrint, setDidOpenPrint] = React.useState(false);

  React.useEffect(() => {
    if (orderIds.length === 0) return;
    const chunks = chunk(orderIds, CHUNK_SIZE);
    const results = new Array(chunks.length);
    let cancelled = false;
    let nextChunk = 0;

    const worker = async () => {
      for (;;) {
        const index = nextChunk++;
        if (cancelled || index >= chunks.length) return;
        try {
          results[index] = await post("order-profile/batch", {
            order_ids: chunks[index]
          });
        } catch (e) {
          // A failed chunk loses those orders, not the whole job. Shaped like
          // the per-order failures the server reports so both render the same.
          results[index] = chunks[index].map(orderId => ({
            order_id: orderId,
            error: "This order could not be loaded."
          }));
        }
        if (!cancelled) setLoadedCount(count => count + chunks[index].length);
      }
    };

    (async () => {
      await Promise.all(
        Array.from({ length: Math.min(CONCURRENCY, chunks.length) }, worker)
      );
      if (cancelled) return;
      setOrders([].concat(...results.filter(Boolean)));
      setIsFetching(false);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    if (isFetching || didOpenPrint || orderIds.length === 0) return;
    // This effect runs after React commits the profiles to the DOM, but the
    // browser has not necessarily laid them out and painted yet. Printing before
    // paint yields blank or half-drawn pages. Wait for web fonts to load, then
    // for two animation frames — the browser paints between them, so by the
    // second frame the committed profiles are on screen and safe to print.
    let cancelled = false;
    let rafId;
    const printWhenPainted = () => {
      rafId = window.requestAnimationFrame(() => {
        rafId = window.requestAnimationFrame(() => {
          if (cancelled) return;
          window.print();
          setDidOpenPrint(true);
        });
      });
    };
    const fontsReady = document.fonts ? document.fonts.ready : Promise.resolve();
    fontsReady.then(() => {
      if (!cancelled) printWhenPainted();
    });
    return () => {
      cancelled = true;
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, [isFetching, didOpenPrint, orderIds.length]);

  if (orderIds.length === 0)
    return (
      <ErrorMessage>
        No orders were selected for printing. Please return to the orders table,
        select the orders you want to print, and try again.
      </ErrorMessage>
    );

  const failedCount = orders.filter(order => order.error).length;

  return (
    // Single root so the flex `section[role="main"]` gives the whole screen one
    // full-width column; without it each order became its own flex item and the
    // orders laid out side by side.
    <div className="batch-print-orders">
      {isFetching && (
        <div className="no-print">
          <Waiting
            reason={`Getting Orders Ready for Print... (${loadedCount}/${orderIds.length})`}
          />
        </div>
      )}
      {!isFetching && failedCount > 0 && (
        <div className="no-print">
          <ErrorMessage>
            {`${failedCount} of ${orderIds.length} orders could not be loaded and are not included in this print job.`}
          </ErrorMessage>
        </div>
      )}
      {orders
        .filter(order => !order.error && order.profile)
        .map(order => (
          // Each order is a full-width block that starts on a fresh page, so
          // orders stack vertically and two orders never share a printed page.
          <div className="batch-print-order" key={order.order_id}>
            <OrderProfileNew
              orderId={order.order_id}
              printMode
              seed={{
                order: order.profile,
                lineItems: order.line_items,
                tasks: order.tasks
              }}
            />
          </div>
        ))}
    </div>
  );
};

export default BatchPrintOrders;
